import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const DatePickerCalendar = ({ value, onChange, minDate, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom'); // 'bottom' | 'top'
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  const today = new Date();
  const min = minDate ? new Date(minDate) : today;
  const selected = value ? new Date(value + 'T00:00:00') : null;

  const [viewMonth, setViewMonth] = useState(selected ? selected.getMonth() : today.getMonth());
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : today.getFullYear());

  // Close on outside click (mousedown + touchstart for iPad kiosk parity)
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // When the dropdown opens, decide whether to place it below or above the trigger
  // based on available viewport space. This handles the iPad-landscape case where
  // the trigger sits mid-screen and a downward drop would clip.
  useEffect(() => {
    if (!open || !ref.current) {
      setPlacement('bottom');
      return;
    }
    const decide = () => {
      const triggerRect = ref.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.offsetHeight || 380; // fallback estimate
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      // Flip up when: below would overflow AND above has more room
      if (spaceBelow < dropdownHeight + 16 && spaceAbove > spaceBelow) {
        setPlacement('top');
      } else {
        setPlacement('bottom');
      }
    };
    // Measure after the dropdown mounts so offsetHeight is accurate
    const t = setTimeout(decide, 30);
    window.addEventListener('resize', decide);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', decide);
    };
  }, [open]);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const selectDay = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${viewYear}-${m}-${d}`);
    setOpen(false);
  };

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    return d < new Date(min.getFullYear(), min.getMonth(), min.getDate());
  };

  const isSelected = (day) => {
    if (!selected) return false;
    return selected.getDate() === day && selected.getMonth() === viewMonth && selected.getFullYear() === viewYear;
  };

  const isToday = (day) => {
    return today.getDate() === day && today.getMonth() === viewMonth && today.getFullYear() === viewYear;
  };

  const formatDisplay = () => {
    if (!value) return '';
    const d = new Date(value + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Year range: current year to +5
  const years = [];
  for (let y = today.getFullYear(); y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Input trigger */}
      <button
        type="button"
        data-testid="datepicker-trigger"
        onClick={() => setOpen(!open)}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3.5 text-left flex items-center gap-3 hover:border-gold/50 focus:border-gold focus:ring-1 focus:ring-gold transition-all"
      >
        <Calendar className="w-5 h-5 text-white/50 flex-shrink-0" />
        <span className={value ? 'text-white' : 'text-white/40'}>
          {value ? formatDisplay() : 'Select your date'}
        </span>
      </button>

      {/* Calendar dropdown — auto-flips upward when downward placement would overflow */}
      {open && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 right-0 z-50 rounded-xl border border-white/20 shadow-2xl overflow-hidden animate-fade-up ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{ backgroundColor: '#2A2A2A' }}
        >
          {/* Header with month/year dropdowns */}
          <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
            <button type="button" onClick={prevMonth} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 min-h-[44px] text-white text-sm focus:outline-none focus:border-gold appearance-none cursor-pointer"
                style={{ WebkitAppearance: 'none' }}
              >
                {MONTHS.map((m, i) => <option key={i} value={i} className="bg-neutral-800 text-white">{m}</option>)}
              </select>
              <select
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 min-h-[44px] text-white text-sm focus:outline-none focus:border-gold appearance-none cursor-pointer"
                style={{ WebkitAppearance: 'none' }}
              >
                {years.map(y => <option key={y} value={y} className="bg-neutral-800 text-white">{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={nextMonth} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 px-3 pt-2">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs text-white/40 font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 px-3 pb-3">
            {/* Empty cells for offset */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[44px]" />
            ))}
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const disabled = isDisabled(day);
              const sel = isSelected(day);
              const tod = isToday(day);
              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => selectDay(day)}
                  className={`min-h-[44px] flex items-center justify-center text-sm rounded-lg transition-all duration-150 ${
                    disabled
                      ? 'text-white/15 cursor-not-allowed'
                      : sel
                        ? 'bg-gold text-black font-bold'
                        : tod
                          ? 'bg-white/10 text-gold font-semibold hover:bg-gold/20'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerCalendar;
